import React, { useRef, useState } from "react";
import { Column, Row, SizedBox } from "../CommonBaseComponents";
import { C3Text, C4Text } from "../text/CustomText";
import CustomIcon from "../image/CustomIcon";
import styled from "styled-components";
import { Theme } from "../../Theme";
import SecondaryButton from "../button/SecondaryButton";
import PrimaryButton from "../button/PrimaryButton";
import Translate from "../../translate/Translate";
import CustomDropdownMenu from "../dropdown/CustomDropdownMenu";

const InputContainer = styled.div`
    display: flex;
    align-items: center;
    padding: ${({ padding }) => padding ?? "12px 16px"};
    border: ${(props) => (props.disabled ? `1px solid ${Theme.color.neutral100}` : `1px solid ${Theme.color.gray200}`)};
    border-radius: ${(props) => props.borderRadius || "8px"};
    background-color: ${(props) => (props.disabled ? Theme.color.neutral100 : "transparent")};
    width: ${(props) => props.width || "100%"};
    box-sizing: border-box;
    position: relative;

    &:focus-within {
        border-color: #6250fc;
    }
`;

const StyledInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    font-size: ${(props) => props.fontSize || Theme.text.c3.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c3.fontWeight};
    color: ${(props) => props.fontColor || Theme.text.default.fontColor};
    background-color: transparent;
    width: ${(props) => props.innerWidth || "100%"};
    cursor: ${({ $cursor }) => $cursor || "text"};

    &::placeholder {
        font-size: ${(props) => props.placeHolderFontSize || Theme.text.c3.fontSize};
        font-family: ${(props) => props.placeHolderFontFamily || Theme.text.default.fontFamily};
        letter-spacing: ${(props) => props.placeHolderLetterSpacing || Theme.text.default.letterSpacing};
        line-height: ${(props) => props.placeHolderLineHeight || Theme.text.default.lineHeight};
        font-weight: ${(props) => props.placeHolderFontWeight || Theme.text.c3.fontWeight};
        color: ${(props) => props.placeHolderFontColor || Theme.color.gray500};
    }
`;

const StyledInlineTextArea = styled.textarea`
    flex: 1;
    border: none;
    outline: none;
    font-size: ${(props) => props.fontSize || Theme.text.c3.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c3.fontWeight};
    color: ${(props) => props.fontColor || Theme.text.default.fontColor};
    background-color: transparent;
    width: 100%;
    resize: none;
    overflow: hidden;

    &::placeholder {
        font-size: ${(props) => props.placeHolderFontSize || Theme.text.c3.fontSize};
        font-family: ${(props) => props.placeHolderFontFamily || Theme.text.default.fontFamily};
        letter-spacing: ${(props) => props.placeHolderLetterSpacing || Theme.text.default.letterSpacing};
        line-height: ${(props) => props.placeHolderLineHeight || Theme.text.default.lineHeight};
        font-weight: ${(props) => props.placeHolderFontWeight || Theme.text.c3.fontWeight};
        color: ${(props) => props.placeHolderFontColor || Theme.color.gray500};
    }

    &:disabled {
        background-color: #f6f6f6;
    }
`;

const StyledTextArea = styled.textarea`
    width: 100%;
    min-height: 120px;
    padding: 12px 16px;
    box-sizing: border-box;

    border: 1px solid #e6e6e9;
    border-radius: 8px;
    background-color: transparent;

    font-size: ${(props) => props.fontSize || Theme.text.c3.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c3.fontWeight};
    color: ${(props) => props.fontColor || Theme.text.default.fontColor};

    resize: none;

    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #aaa;
        border-radius: 4px;
    }

    &::placeholder {
        font-size: ${(props) => props.placeHolderFontSize || Theme.text.c3.fontSize};
        font-family: ${(props) => props.placeHolderFontFamily || Theme.text.default.fontFamily};
        letter-spacing: ${(props) => props.placeHolderLetterSpacing || Theme.text.default.letterSpacing};
        line-height: ${(props) => props.placeHolderLineHeight || Theme.text.default.lineHeight};
        font-weight: ${(props) => props.placeHolderFontWeight || Theme.text.c3.fontWeight};
        color: ${(props) => props.placeHolderFontColor || Theme.color.gray500};
    }

    &:focus {
        border-color: #007bff;
        outline: none;
    }

    &:disabled {
        background-color: #f6f6f6;
        border: none;
    }
`;

const NonBorderTextAreaContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border: ${(props) => (props.disabled ? `1px solid ${Theme.color.neutral100}` : `1px solid ${Theme.color.gray200}`)};
    border-radius: ${(props) => props.borderRadius || "8px"};
    background-color: ${(props) => (props.disabled ? Theme.color.neutral100 : "transparent")};
    width: 100%;
    box-sizing: border-box;
    position: relative;
    flex-direction: column;

    &:focus-within {
        border-color: #6250fc;
    }
`;

const NonBorderTextArea = styled.textarea`
    width: 100%;
    min-height: 60px;
    box-sizing: border-box;
    border: none;
    resize: none;

    font-size: ${(props) => props.fontSize || Theme.text.c3.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c3.fontWeight};
    color: ${(props) => props.fontColor || Theme.text.default.fontColor};

    &::placeholder {
        font-size: ${(props) => props.placeHolderFontSize || Theme.text.c3.fontSize};
        font-family: ${(props) => props.placeHolderFontFamily || Theme.text.default.fontFamily};
        letter-spacing: ${(props) => props.placeHolderLetterSpacing || Theme.text.default.letterSpacing};
        line-height: ${(props) => props.placeHolderLineHeight || Theme.text.default.lineHeight};
        font-weight: ${(props) => props.placeHolderFontWeight || Theme.text.c3.fontWeight};
        color: ${(props) => props.placeHolderFontColor || Theme.color.gray500};
    }

    &:focus {
        border-color: transparent;
        outline: none;
    }
`;

function CustomInput({
    placeholder,
    type,
    dropdownValues,
    inputValue,
    setInputValue,
    disabled,
    onDelete,
    noBlur,
    ...props
}) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const inputRef = useRef(null); // ref for the input container

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    const handleValueSelect = (value) => {
        setInputValue(value);
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight - 5}px`;
    };

    const handleBirthChange = (e) => {
        // 이전 값과 새 입력값에서 숫자만 추출
        const prevDigitsOnly = inputValue.replace(/[^0-9]/g, "");
        let digitsOnly = e.target.value.replace(/[^0-9]/g, "");

        const isAdding = digitsOnly.length > prevDigitsOnly.length;

        // --- 자동 완성 로직 (문자 추가 시에만 적용) ---
        if (isAdding) {
            // 1. 월 자동 완성
            if (prevDigitsOnly.length === 4 && digitsOnly.length === 5) {
                const monthInitialDigit = digitsOnly.slice(4, 5);
                // 사용자가 2~9 사이 숫자를 입력하면 '0'을 앞에 붙여줌 (예: 3 -> 03)
                if (parseInt(monthInitialDigit, 10) > 1) {
                    const yearPart = digitsOnly.slice(0, 4);
                    digitsOnly = `${yearPart}0${monthInitialDigit}`;
                }
            }

            // 2. 일 자동 완성
            if (prevDigitsOnly.length === 6 && digitsOnly.length === 7) {
                const dayInitialDigit = digitsOnly.slice(6, 7);
                const dayInitialDigitInt = parseInt(dayInitialDigit, 10);
                const month = parseInt(digitsOnly.slice(4, 6), 10);

                let shouldPrependZero = false;
                // 사용자가 4~9 사이 숫자를 입력하면 '0'을 앞에 붙여줌 (예: 6 -> 06)
                if (dayInitialDigitInt > 3) {
                    shouldPrependZero = true;
                }
                // 2월의 경우, 3을 입력해도 '0'을 앞에 붙여줌 (예: 3 -> 03)
                else if (dayInitialDigitInt === 3 && month === 2) {
                    shouldPrependZero = true;
                }

                if (shouldPrependZero) {
                    const datePart = digitsOnly.slice(0, 6);
                    digitsOnly = `${datePart}0${dayInitialDigit}`;
                }
            }
        }

        // --- 유효성 검사 및 값 보정 (기존 로직) ---

        // 연도 (Year) 유효성 검사
        if (digitsOnly.length >= 4) {
            const year = parseInt(digitsOnly.substring(0, 4), 10);
            const currentYear = new Date().getFullYear();
            if ((year < 1900 && digitsOnly.length === 4) || year > currentYear) {
                digitsOnly = digitsOnly.slice(0, 3);
            }
        }

        // 월 (Month) 유효성 검사
        if (digitsOnly.length >= 6) {
            const month = parseInt(digitsOnly.substring(4, 6), 10);
            if (month === 0 || month > 12) {
                digitsOnly = digitsOnly.slice(0, 5);
            }
        }

        // 일 (Day) 유효성 검사
        if (digitsOnly.length >= 8) {
            const year = parseInt(digitsOnly.substring(0, 4), 10);
            const month = parseInt(digitsOnly.substring(4, 6), 10);
            const day = parseInt(digitsOnly.substring(6, 8), 10);

            if (day === 0) {
                digitsOnly = digitsOnly.slice(0, 7);
            } else if (month > 0 && month <= 12) {
                const lastDayOfMonth = new Date(year, month, 0).getDate();
                if (day > lastDayOfMonth) {
                    digitsOnly = digitsOnly.slice(0, 7);
                }
            }
        }

        // --- 형식 맞추기 (기존 로직) ---
        let formattedValue = digitsOnly;
        if (digitsOnly.length > 4) {
            formattedValue = `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4)}`;
        }
        if (digitsOnly.length > 6) {
            formattedValue = `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4, 6)}.${digitsOnly.slice(6, 8)}`;
        }

        // 최종 상태 업데이트
        setInputValue(formattedValue);
    };

    // 패스워드 visibility
    const [visibility, setVisibility] = useState(false);

    if (type === "search") {
        return (
            <InputContainer ref={inputRef} disabled={disabled} padding={props.padding}>
                {!disabled && !props.isSearchIconLeft && (
                    <>
                        <CustomIcon iconName={"search"} size={"12px"} />
                        <SizedBox width={"10px"} />
                    </>
                )}
                <StyledInput
                    type={props.inputType || "text"}
                    placeholder={placeholder}
                    onFocus={() => setIsMenuOpen(true)}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") props.onSearch();
                    }}
                    {...props}
                    disabled={disabled}
                />
                {!disabled && props.isSearchIconLeft && (
                    <>
                        <CustomIcon iconName={"search"} size={"12px"} onClick={props.onSearch} cursor={"pointer"} />
                    </>
                )}
                {isMenuOpen && dropdownValues && dropdownValues.length > 0 && (
                    <CustomDropdownMenu
                        onClose={toggleMenu}
                        values={dropdownValues}
                        onSelect={handleValueSelect}
                        noDivider={props.noDivider}
                        noBlur={noBlur}
                        anchorEl={inputRef.current}
                    />
                )}
            </InputContainer>
        );
    } else if (type === "normal") {
        return (
            <>
                <InputContainer disabled={disabled} padding={props.padding}>
                    <StyledInput
                        type={props.inputType || "text"}
                        placeholder={placeholder}
                        disabled={disabled}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        {...props}
                    />
                    {onDelete && (
                        <>
                            <SizedBox width={"8px"} />
                            <CustomIcon
                                iconName={"close"}
                                onClick={onDelete}
                                size={"16px"}
                                cursor={"pointer"}
                            />
                        </>
                    )}
                </InputContainer>
                {!props.isValid && (
                    <>
                        <SizedBox height={"6px"} />
                        <C3Text fontColor={Theme.color.rose500} fontWeight={"400"}>
                            {props.errorText}
                        </C3Text>
                    </>
                )}
            </>
        );
    } else if (type === "password") {
        return (
            <>
                <InputContainer disabled={disabled} padding={props.padding}>
                    <StyledInput
                        type={visibility ? "text" : "password"}
                        placeholder={placeholder}
                        disabled={disabled}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        {...props}
                    />
                    <CustomIcon
                        iconName={visibility ? "visibility_on" : "visibility_off"}
                        width={"20px"}
                        height={"20px"}
                        cursor={"pointer"}
                        onClick={() => setVisibility(!visibility)}
                    />
                </InputContainer>
                {!props.isValid && (
                    <>
                        <SizedBox height={"6px"} />
                        <C3Text fontColor={Theme.color.rose500} fontWeight={"400"}>
                            Please enter a valid password.
                        </C3Text>
                    </>
                )}
            </>
        );
    } else if (type === "confirmPassword") {
        return (
            <>
                <InputContainer disabled={disabled} padding={props.padding}>
                    <StyledInput
                        type={visibility ? "text" : "password"}
                        placeholder={placeholder}
                        disabled={disabled}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        {...props}
                    />
                    <CustomIcon
                        iconName={visibility ? "visibility_on" : "visibility_off"}
                        width={"20px"}
                        height={"20px"}
                        cursor={"pointer"}
                        onClick={() => setVisibility(!visibility)}
                    />
                </InputContainer>
                {!props.isValid && (
                    <>
                        <SizedBox height={"6px"} />
                        <C3Text fontColor={Theme.color.rose500} fontWeight={"400"}>
                            Password do not matched.
                        </C3Text>
                    </>
                )}
            </>
        );
    } else if (type === "email") {
        return (
            <>
                <InputContainer disabled={disabled} padding={props.padding}>
                    <StyledInput
                        type="email"
                        placeholder={placeholder}
                        disabled={disabled}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        {...props}
                    />
                </InputContainer>
                {!props.isValid && (
                    <>
                        <SizedBox height={"6px"} />
                        <C3Text fontColor={Theme.color.rose500} fontWeight={"400"}>
                            Please enter a valid email address.
                        </C3Text>
                    </>
                )}
            </>
        );
    } else if (type === "select") {
        if (!dropdownValues) {
            return <C3Text>dropdownValues 속성 누락</C3Text>;
        }

        return (
            <InputContainer ref={inputRef} disabled={disabled} padding={props.padding} width={props.width}>
                <StyledInput
                    type={props.inputType || "text"}
                    placeholder={placeholder}
                    onClick={() => setIsMenuOpen((prev) => !prev)}
                    value={dropdownValues.find((value) => value.value === inputValue)?.label}
                    onChange={(e) => setInputValue(e.target.value)}
                    readOnly={true}
                    disabled={disabled}
                    fontColor={dropdownValues.find((value) => value.value === inputValue)?.color ?? Theme.color.zinc850}
                    $cursor={"pointer"}
                    innerWidth={props.innerWidth}
                    {...props}
                />
                {isMenuOpen && (
                    <CustomDropdownMenu
                        onClose={toggleMenu}
                        values={dropdownValues}
                        onSelect={handleValueSelect}
                        currentValue={inputValue}
                        noDivider={props.noDivider}
                        noBlur={noBlur}
                        anchorEl={inputRef.current}
                    />
                )}
                {!disabled && (
                    <CustomIcon
                        iconName={isMenuOpen ? "chevron_up" : "chevron_down"}
                        size={"18px"}
                        onClick={() => {
                            if (disabled) {
                                return;
                            }
                            setIsMenuOpen((prev) => !prev);
                        }}
                    />
                )}
            </InputContainer>
        );
    } else if (type === "textAria") {
        return (
            <StyledTextArea
                type={props.inputType || "text"}
                placeholder={placeholder}
                onFocus={() => setIsMenuOpen(true)}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={disabled}
                {...props}
            />
        );
    } else if (type === "memo") {
        return (
            <Column alignItem={"flex-start"} width={"100%"}>
                {disabled && (
                    <>
                        <C4Text>{props.createdAt}</C4Text>
                        <SizedBox height={"6px"} />
                    </>
                )}
                <InputContainer disabled={disabled} borderRadius={"12px"} padding={props.padding}>
                    <StyledInlineTextArea
                        rows={1}
                        type={props.inputType || "text"}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        disabled={disabled}
                        {...props}
                    />
                    {!disabled && <CustomIcon iconName={"close"} onClick={onDelete} size={"16px"} cursor={"pointer"} />}
                </InputContainer>
            </Column>
        );
    } else if (type === "file") {
        return (
            <InputContainer disabled={disabled} padding={props.padding}>
                <StyledInput
                    type={props.inputType || "text"}
                    placeholder={placeholder}
                    disabled={disabled}
                    value={inputValue}
                    readOnly={true}
                    onChange={(e) => setInputValue(e.target.value)}
                    {...props}
                />
            </InputContainer>
        );
    } else if (type === "textAriaWithButton") {
        return (
            <NonBorderTextAreaContainer>
                <NonBorderTextArea
                    type={props.inputType || "text"}
                    placeholder={placeholder}
                    disabled={disabled}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                />
                <Row alignItem={"flex-end"} width={"100%"} justifyContent={"flex-end"}>
                    <SecondaryButton text={props.deleteButonText} onClick={onDelete} />
                    <SizedBox width={"10px"} />
                    <PrimaryButton text={Translate("common.save")} onClick={props.onSave} />
                </Row>
            </NonBorderTextAreaContainer>
        );
    } else if (type === "birth") {
        return (
            <InputContainer disabled={disabled} padding={props.padding}>
                <CustomIcon iconName={"calendar"} size={"18px"} />
                <SizedBox width={"6px"} />
                <StyledInput
                    type={props.inputType || "text"}
                    placeholder={"YYYY.MM.DD"}
                    disabled={disabled}
                    value={inputValue}
                    onChange={handleBirthChange}
                    {...props}
                />
            </InputContainer>
        );
    } else {
        return (
            <C3Text>
                현재 선택 가능한 타입 search / normal / password / select / textAria / memo / textAriaWithButton
            </C3Text>
        );
    }
}

export default CustomInput;
